import { addTimingsToNetworkRequest } from 'owa-analytics-shared';
import type { ServiceResponseCallback, TraceErrorObject } from 'owa-analytics-types';
import { isRunningOnWorker } from 'owa-config/lib/isRunningOnWorker';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { setOwaNetVersion } from 'owa-notification-globals';
import type { ServiceRequestConfig } from './config';
import { getConfig } from './config';
import createOptionsForServiceRequest from './createOptionsForServiceRequest';
import fetchHandler from './fetchHandler';
import getRequestNumber from './getRequestNumber';
import isActivityTimeoutAuthError from './isActivityTimeoutAuthError';
import type MailboxRequestOptions from './MailboxRequestOptions';
import type RequestOptions from './RequestOptions';
import type { InternalRequestOptions } from './RequestOptions';
import { getRetryStrategy, type OnRetry } from './retryStrategy';
import throttledFetch from './throttledFetch';

let serviceResponseCallbackNumber = 0;
const createServiceResponseCallbacks: {
    [key: number]: ServiceResponseCallback;
} = {};

// this returns a function that will unregister it
export function registerCreateServiceResponseCallback(
    callback: ServiceResponseCallback
): () => void {
    const id = serviceResponseCallbackNumber++;
    createServiceResponseCallbacks[id] = callback;

    return () => {
        delete createServiceResponseCallbacks[id];
    };
}

export default async function fetchWithRetry(
    actionName: string,
    originalUrl: string,
    attemptCount: number,
    requestOptions: RequestOptions | MailboxRequestOptions | undefined,
    parameters: any
): Promise<any> {
    const config = getConfig();

    let shouldRetry: boolean;
    let targetEndpoint = originalUrl;
    let callCount = attemptCount;
    let optionsPromise: Promise<InternalRequestOptions> | undefined;
    let response: Response | undefined;
    let retryEndpoint: string | undefined;
    let onRetry: OnRetry | undefined;

    do {
        let err: TraceErrorObject | undefined;
        shouldRetry = false;

        optionsPromise = createOptionsForServiceRequest(
            optionsPromise ? await optionsPromise : requestOptions,
            parameters,
            actionName
        );

        try {
            const dp = requestOptions?.perfDatapoint;
            const datapointIsDefined = dp?.datapoint || dp?.customDatapoint;

            if (datapointIsDefined) {
                const settings = {
                    discardIfDefined: dp.discardIfDefined || false,
                };

                response = await addTimingsToNetworkRequest(
                    {
                        datapoint: dp.datapoint,
                        customDatapoint: dp.customDatapoint,
                    },
                    settings,
                    doFetch,
                    actionName,
                    targetEndpoint,
                    callCount,
                    parameters,
                    config,
                    optionsPromise
                );
            } else {
                response = await doFetch(
                    actionName,
                    targetEndpoint,
                    callCount,
                    parameters,
                    config,
                    optionsPromise
                );
            }
        } catch (e) {
            err = e as TraceErrorObject;
        }

        ({ shouldRetry, retryEndpoint, onRetry } = await getRetryStrategy(
            err,
            callCount,
            response,
            config,
            requestOptions,
            await optionsPromise
        ));

        // bail out of the retry loop if we shouldn't retry or exceed the max retry count
        if (!shouldRetry) {
            // throw original error if we have one, otherwise let the response be handled by fetchHandler
            if (err) {
                throw err;
            }
            break;
        }

        // at this point, we are going to retry
        callCount++;
        targetEndpoint = retryEndpoint ?? originalUrl;

        // invoke the onRetry callback to do any work needed before the next retry
        await onRetry?.();
    } while (shouldRetry);

    if (!response) {
        throw new Error('fetchWithRetry: No response returned from doFetch.');
    }

    // Everything is fine until this point, we will let fetchHandler deal with the response
    // We would like to know where this function was invoked from so let's create the callstack here
    // and pass it through just in case we need it.
    return fetchHandler<any>(
        actionName,
        response,
        await optionsPromise,
        new Error('OwsApiRequestCompleted').stack
    );
}

async function doFetch(
    actionName: string,
    originalUrl: string,
    callCount: number,
    parameters: any,
    config: ServiceRequestConfig,
    optionsPromise: Promise<InternalRequestOptions>
): Promise<Response> {
    const url = originalUrl + '&n=' + getRequestNumber();
    const fetchPromise = throttledFetch(url, optionsPromise);

    invokeServiceResponseCallback(
        actionName,
        url,
        callCount,
        parameters,
        optionsPromise,
        fetchPromise
    );

    const options: InternalRequestOptions = await optionsPromise;
    const response: Response = await fetchPromise;

    if (isActivityTimeoutAuthError(response.status, response.headers)) {
        await config?.onActivityTimeoutError?.();
    } else {
        // Go through federated logout flow if we have a federated logout header (enterprise)
        // or if we have a logouuSid cookie (consumer).
        // onFederaedLogout will run on the main thread. In the enterprise scenario, we need to
        // be able to switch from a worker thread to the main thread, but we don't want to do this
        // every time for performance reasons.
        const sidHeader = response?.headers?.get('x-owa-logoutSid');
        const shouldCheckFedLogout = sidHeader || !isRunningOnWorker();

        if (shouldCheckFedLogout && config?.onFederatedLogout && options.mailboxInfo) {
            const fedLogoutResult = await config.onFederatedLogout(
                response.url,
                options.mailboxInfo,
                sidHeader
            );
            if (fedLogoutResult) {
                throw response;
            }
        }
    }

    const responseOrigin = response.headers?.get('x-responseorigin');

    if (
        response.url?.includes('/owa/') &&
        responseOrigin &&
        options.mailboxInfo &&
        isSuccessStatusCode(response.status)
    ) {
        setOwaNetVersion(responseOrigin, options.mailboxInfo);
    }

    return response;
}

function invokeServiceResponseCallback(
    actionName: string,
    url: string,
    callCount: number,
    parameters: any,
    optionsPromise: Promise<InternalRequestOptions>,
    fetchPromise: Promise<Response>
) {
    let logActionName = actionName;

    // Use the Update Field to indicate the actual UpdateItem action for this OWS service request.
    if (actionName == 'UpdateItem') {
        logActionName =
            actionName + parameters?.Body?.ItemChanges?.[0]?.Updates?.[0]?.Path?.FieldURI || '';
    }

    for (const callback of Object.values<ServiceResponseCallback>(createServiceResponseCallbacks)) {
        callback('OWSClassic', fetchPromise, logActionName, url, callCount, optionsPromise);
    }
}
